import { CheckCircle, Search, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ProfileImage } from '../ui';

const EntitySelectionDialog = ({
  isOpen,
  onClose,
  onConfirm,
  entityType,
  dialogTitle,
  availableEntities
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFilteredEntities(availableEntities);
      setSearchTerm('');
      setSelectedEntityId(null);
    }
  }, [isOpen, availableEntities]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredEntities(availableEntities);
      return;
    }

    const query = searchTerm.toLowerCase();
    const filtered = availableEntities.filter(entity => {
      switch (entityType.toLowerCase()) {
        case 'employee':
          const id = (entity.user_id || '').toLowerCase();
          const name = (entity.user_name || entity.name || '').toLowerCase();
          const role = (entity.spec_role || '').toLowerCase();
          return id.includes(query) || name.includes(query) || role.includes(query);
        case 'vehicle':
          const vehicleId = (entity.vehicle_id || '').toLowerCase();
          const registrationNo = (entity.registration_no || '').toLowerCase();
          const model = (entity.model_name || '').toLowerCase();
          return vehicleId.includes(query) || registrationNo.includes(query) || model.includes(query);
        case 'site':
          const siteId = (entity.site_id || '').toLowerCase();
          const siteName = (entity.site_name || '').toLowerCase();
          const siteType = (entity.site_type || '').toLowerCase();
          return siteId.includes(query) || siteName.includes(query) || siteType.includes(query);
        default:
          return true;
      }
    });
    setFilteredEntities(filtered);
  }, [searchTerm, availableEntities, entityType]);

  const getEntityId = (entity) => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return entity.user_id || '';
      case 'vehicle':
        return entity.vehicle_id || '';
      case 'site':
        return entity.site_id || '';
      default:
        return '';
    }
  };

  const getEntityTitle = (entity) => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return entity.user_name || entity.name || 'Unknown';
      case 'vehicle':
        return `${entity.registration_no || 'Unknown'} (ID: ${entity.vehicle_id || 'N/A'})`;
      case 'site':
        return entity.site_name || 'Unknown';
      default:
        return 'Unknown';
    }
  };

  const getEntitySubtitle = (entity) => {
    switch (entityType.toLowerCase()) {
      case 'employee':
        return entity.spec_role || 'Employee';
      case 'vehicle':
        return `${entity.vehicle_type || 'Vehicle'} - Model: ${entity.model_name || 'N/A'}`;
      case 'site':
        return `Location: ${entity.address || 'N/A'}`;
      default:
        return '';
    }
  };

  const handleConfirm = () => {
    if (selectedEntityId) {
      onConfirm(selectedEntityId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{dialogTitle}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${entityType.toLowerCase()}...`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Entity List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredEntities.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No {entityType.toLowerCase()}s found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEntities.map((entity) => {
                const entityId = getEntityId(entity);
                const isSelected = selectedEntityId === entityId;

                return (
                  <div
                    key={entityId}
                    onClick={() => setSelectedEntityId(entityId)}
                    className={`
                      p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-green-600 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar/Icon */}
                      {entityType.toLowerCase() === 'employee' && (
                        <ProfileImage
                          userId={entity.user_id}
                          userName={entity.user_name || entity.name}
                          className="w-10 h-10"
                          showLoading={false}
                        />
                      )}
                      {entityType.toLowerCase() === 'vehicle' && (
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 text-lg">🚗</span>
                        </div>
                      )}
                      {entityType.toLowerCase() === 'site' && (
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-green-600 text-lg">📍</span>
                        </div>
                      )}

                      {/* Entity Info */}
                      <div className="flex-1">
                        <p className={`font-medium ${isSelected ? 'text-green-700' : 'text-gray-900'}`}>
                          {getEntityTitle(entity)}
                        </p>
                        <p className={`text-sm ${isSelected ? 'text-green-600' : 'text-gray-600'}`}>
                          {getEntitySubtitle(entity)}
                        </p>
                      </div>

                      {/* Check Icon */}
                      {isSelected && (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleConfirm}
            disabled={!selectedEntityId}
            className={`
              px-6 py-2 rounded-lg font-medium transition-colors
              ${selectedEntityId
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntitySelectionDialog;

